import { DatePicker, Flex, Switch, Table } from 'antd'
import { Dayjs } from 'dayjs'
import { useState } from 'react'

import { useTreatmentServiceGetNewbornInvestigationStatusListEndpoint } from 'src/api/queries'
import {
  NewbornInvestigationPeriod,
  NewbornInvestigationStatusItemDto
} from 'src/api/requests'
import { ColumnTableFilter, SearchBar, Select, Text } from 'src/components'
import {
  useTableColumnsVisibility,
  useTablePagination,
  useUser
} from 'src/hooks'
import { dayMonthYearFormatWithDot, yearMonthDayTimeWithT } from 'src/libs'
import { useLocales } from 'src/locales'
import { resetPagination } from 'src/utils'

import { useNewbornInvestigationStatusColumns } from '../hooks/useNewbornInvestigationStatusColumns'

type BirthDateRange = [Dayjs | null, Dayjs | null] | null

export const NewbornInvestigationStatusTab = () => {
  const { t } = useLocales()
  const { userData } = useUser()

  const pagination = useTablePagination()

  const [search, setSearch] = useState('')
  const [onlyIncomplete, setOnlyIncomplete] = useState(false)
  const [period, setPeriod] = useState<NewbornInvestigationPeriod>()
  const [birthDate, setBirthDate] = useState<BirthDateRange>(null)

  const { columns } = useNewbornInvestigationStatusColumns()
  const { newColumns, hiddenColumns, onVisibilityColumnsChange } =
    useTableColumnsVisibility(columns)

  const columnOptions = columns.map(({ key, title }) => ({
    label: title,
    value: String(key)
  }))

  const birthDateFrom = birthDate?.[0]?.startOf('day')
  const birthDateTo = birthDate?.[1]?.endOf('day')

  const queryParams = {
    pageNumber: Number(pagination.current),
    pageSize: Number(pagination.pageSize),
    institutionIds: [String(userData?.currentLegalEntityId)],
    ...(!!search && { searchTerm: search }),
    ...(onlyIncomplete && { onlyIncomplete }),
    ...(!!period && { period }),
    ...(!!birthDateFrom && {
      birthDateFrom: birthDateFrom.format(yearMonthDayTimeWithT)
    }),
    ...(!!birthDateTo && {
      birthDateTo: birthDateTo.format(yearMonthDayTimeWithT)
    })
  }

  const { data, isLoading } =
    useTreatmentServiceGetNewbornInvestigationStatusListEndpoint(
      queryParams,
      [queryParams],
      { enabled: !!userData?.currentLegalEntityId }
    )

  const onFilterChange = <T,>(value: T, setState: (value: T) => void) => {
    setState(value)
    resetPagination(pagination)
  }

  const periodOptions = Object.values(NewbornInvestigationPeriod).map(
    (value) => ({
      value,
      label: t(`pregnancy.newborn_period_status.${value}`)
    })
  )

  return (
    <Flex vertical gap={16}>
      <Flex gap={16} justify="space-between" wrap="wrap">
        <Flex gap={8} wrap="wrap" align="center">
          <SearchBar
            maxWidth={280}
            placeholder={t(
              'pregnancy.newborn_investigation_status.search_placeholder'
            )}
            onSearch={(value) => onFilterChange(value, setSearch)}
          />
          <DatePicker.RangePicker
            format={dayMonthYearFormatWithDot}
            value={birthDate}
            onChange={(value) => onFilterChange(value, setBirthDate)}
            placeholder={[
              t('pregnancy.birth_date_from'),
              t('pregnancy.birth_date_to')
            ]}
          />
          <Select
            allowClear
            width={200}
            value={period}
            options={periodOptions}
            placeholder={t('pregnancy.monitoring_period')}
            onChange={(value) => onFilterChange(value, setPeriod)}
          />
          <Flex gap={8} align="center">
            <Switch
              checked={onlyIncomplete}
              onChange={(value) => onFilterChange(value, setOnlyIncomplete)}
            />
            <Text>
              {t('pregnancy.newborn_investigation_status.only_incomplete')}
            </Text>
          </Flex>
        </Flex>

        <ColumnTableFilter
          options={columnOptions}
          hiddenColumns={hiddenColumns}
          onChange={onVisibilityColumnsChange}
        />
      </Flex>

      <Table<NewbornInvestigationStatusItemDto>
        rowKey="newbornId"
        scroll={{ x: 'max-content' }}
        loading={isLoading}
        columns={newColumns}
        dataSource={data?.data}
        pagination={{ ...pagination, total: data?.totalCount }}
      />
    </Flex>
  )
}
